/**
 * Analytics income overview (Task 3.1 / BA-05).
 * Combined membership + retail income list sourced from v_revenue via getRevenue.
 * Searchable, filterable by kind and by product/membership, with expandable rows.
 */
import { getRevenue } from '@gym/api-client';
import type { RevenueRow } from '@gym/api-client';
import { useQuery } from '@tanstack/react-query';
import { DollarSign } from 'lucide-react';
import { Fragment, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExpandableCard } from '../../components/ui/ExpandableCard.js';
import { EmptyState, Skeleton } from '../../components/ui/Feedback.js';
import { Field, Select } from '../../components/ui/Field.js';
import { SearchInput } from '../../components/ui/Layout.js';
import { Money, useFormatters } from '../../components/ui/Money.js';
import { Chip, StatusPill, statusTone } from '../../components/ui/StatusPill.js';
import { Table, TableSkeleton, TableWrap, TD, TH, THead, TR } from '../../components/ui/Table.js';
import { supabase } from '../../lib/supabase.js';
import {
  FISCAL_FILTER_OPTIONS,
  matchesFiscalFilter,
  type FiscalFilter,
} from '../payments/fiscalFilter.js';

// ── Constants ─────────────────────────────────────────────────────────────────

const PAYMENT_KINDS = [
  'membership',
  'retail',
  'pt_session',
  'class_pack',
  'day_pass',
  'other',
] as const;

type ProductFilter = 'all' | 'membership' | 'product';

// ── ExpandedRow — lazy per-row detail (hooks rule: no conditional hooks in loop) ──

interface MembershipDetail {
  plan_id: string | null;
  plan_type: string | null;
  start_date: string | null;
  end_date: string | null;
  price_paid: number | null;
  status: string | null;
}

interface SaleItem {
  name: string;
  quantity: number;
  unit_price: number;
  total: number;
}

function ExpandedRow({ row, colSpan }: { row: RevenueRow; colSpan: number }): ReactNode {
  const { t } = useTranslation();
  const fmt = useFormatters();

  // Membership detail query — enabled only when this row is a membership row
  const membershipQ = useQuery<MembershipDetail | null>({
    queryKey: ['expanded_membership', row.membership_id],
    enabled: row.membership_id != null,
    queryFn: async () => {
      if (!row.membership_id) return null;
      const { data, error } = await supabase
        .from('memberships')
        .select('plan_id, plan_type, start_date, end_date, price_paid, status')
        .eq('id', row.membership_id)
        .single();
      if (error) return null;
      return data as unknown as MembershipDetail;
    },
  });

  // Sale items query — enabled only when this row is a retail/sale row
  const saleItemsQ = useQuery<SaleItem[]>({
    queryKey: ['expanded_sale_items', row.sale_id],
    enabled: row.sale_id != null,
    queryFn: async () => {
      if (!row.sale_id) return [];
      const { data, error } = await supabase
        .from('sale_items')
        .select('name, quantity, unit_price, total')
        .eq('sale_id', row.sale_id);
      if (error) return [];
      return (data ?? []) as unknown as SaleItem[];
    },
  });

  const isLoading = membershipQ.isLoading || saleItemsQ.isLoading;

  return (
    <tr>
      <td colSpan={colSpan} className="bg-surface-2 px-6 py-3 text-sm">
        {isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-3.5 w-48" />
            <Skeleton className="h-3.5 w-32" />
          </div>
        ) : row.membership_id != null ? (
          // Membership detail
          membershipQ.data ? (
            <div className="grid gap-1">
              {membershipQ.data.plan_type ? (
                <p className="text-muted">
                  <span className="font-medium text-fg">{t('memberships.plan')}:</span>{' '}
                  {t(`plans.type.${membershipQ.data.plan_type}`, {
                    defaultValue: membershipQ.data.plan_type,
                  })}
                </p>
              ) : null}
              {membershipQ.data.status ? (
                <p className="text-muted">
                  <span className="font-medium text-fg">{t('memberships.statusLabel')}:</span>{' '}
                  <StatusPill
                    tone={statusTone(membershipQ.data.status)}
                    label={t(`memberships.status.${membershipQ.data.status}`, {
                      defaultValue: membershipQ.data.status,
                    })}
                  />
                </p>
              ) : null}
              {membershipQ.data.start_date ? (
                <p className="text-muted">
                  <span className="font-medium text-fg">{t('memberships.startDate')}:</span>{' '}
                  {fmt.date(membershipQ.data.start_date)}
                </p>
              ) : null}
              {membershipQ.data.end_date ? (
                <p className="text-muted">
                  <span className="font-medium text-fg">{t('memberships.endDate')}:</span>{' '}
                  {fmt.date(membershipQ.data.end_date)}
                </p>
              ) : null}
            </div>
          ) : (
            <p className="text-muted">{t('common.noData')}</p>
          )
        ) : row.sale_id != null ? (
          // Sale line items
          (saleItemsQ.data ?? []).length > 0 ? (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-muted">
                  <th className="pb-1 pr-4 font-semibold">{t('billing.b2b.itemName')}</th>
                  <th className="pb-1 pr-4 font-semibold text-right">{t('retail.quantity')}</th>
                  <th className="pb-1 pr-4 font-semibold text-right">
                    {t('billing.b2b.unitPrice')}
                  </th>
                  <th className="pb-1 font-semibold text-right">{t('billing.b2b.total')}</th>
                </tr>
              </thead>
              <tbody>
                {(saleItemsQ.data ?? []).map((item, i) => (
                  <tr key={i}>
                    <td className="pr-4 py-0.5">{item.name}</td>
                    <td className="pr-4 py-0.5 text-right tnum">{item.quantity}</td>
                    <td className="pr-4 py-0.5 text-right">
                      <Money minor={item.unit_price} />
                    </td>
                    <td className="py-0.5 text-right">
                      <Money minor={item.total} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-muted">{t('common.noData')}</p>
          )
        ) : (
          <p className="text-muted">{t('common.noData')}</p>
        )}
      </td>
    </tr>
  );
}

// ── IncomeOverview ────────────────────────────────────────────────────────────

export function IncomeOverview({ tenantId }: { tenantId: string }): ReactNode {
  const { t } = useTranslation();
  const fmt = useFormatters();

  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [productFilter, setProductFilter] = useState<ProductFilter>('all');
  const [fiscalFilter, setFiscalFilter] = useState<FiscalFilter>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const revenue = useQuery({
    queryKey: ['revenue', tenantId],
    queryFn: () => getRevenue(supabase, tenantId),
    enabled: !!tenantId,
  });

  const rows = revenue.data ?? [];

  // Apply filters
  const filtered = rows.filter((r) => {
    // Kind filter
    if (kindFilter && r.kind !== kindFilter) return false;
    // Product / membership filter
    if (productFilter === 'membership' && r.membership_id == null) return false;
    if (productFilter === 'product' && r.sale_id == null) return false;
    // Fiscalized / non-fiscalized filter
    if (!matchesFiscalFilter(r.fiscal_status, fiscalFilter)) return false;
    // Search filter — over kind, method, amount string
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const haystack = [r.kind, r.method, String(r.amount), r.fiscal_status]
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  const COLS = 5;

  return (
    <ExpandableCard title={t('analytics.incomeOverview.title')}>
      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <SearchInput
          className="w-56 flex-shrink-0"
          placeholder={t('analytics.incomeOverview.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* Kind filter */}
        <Field className="w-44 flex-shrink-0">
          {(id) => (
            <Select id={id} value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
              <option value="">{t('common.all')}</option>
              {PAYMENT_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`analytics.kind.${k}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {/* Product / membership filter */}
        <Field className="w-48 flex-shrink-0">
          {(id) => (
            <Select
              id={id}
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value as ProductFilter)}
            >
              <option value="all">{t('common.all')}</option>
              <option value="membership">{t('analytics.incomeOverview.byMembership')}</option>
              <option value="product">{t('analytics.incomeOverview.byProduct')}</option>
            </Select>
          )}
        </Field>

        {/* Fiscalized / non-fiscalized filter */}
        <Field className="w-44 flex-shrink-0">
          {(id) => (
            <Select
              id={id}
              value={fiscalFilter}
              onChange={(e) => setFiscalFilter(e.target.value as FiscalFilter)}
            >
              {FISCAL_FILTER_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {t(`billing.fiscalFilter.${opt}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      {/* Table */}
      <TableWrap>
        <Table>
          <THead>
            <tr>
              <TH>{t('common.date')}</TH>
              <TH>{t('billing.kind')}</TH>
              <TH>{t('billing.method.label')}</TH>
              <TH numeric>{t('billing.amount')}</TH>
              <TH>{t('analytics.incomeOverview.fiscal')}</TH>
            </tr>
          </THead>
          {revenue.isLoading ? (
            <TableSkeleton rows={6} cols={COLS} />
          ) : (
            <tbody>
              {filtered.map((r) => (
                <Fragment key={r.payment_id}>
                  <TR
                    clickable
                    onClick={() =>
                      setExpandedId((prev) => (prev === r.payment_id ? null : r.payment_id))
                    }
                    aria-expanded={expandedId === r.payment_id}
                  >
                    <TD className="text-muted">{fmt.date(r.paid_at)}</TD>
                    <TD>
                      <Chip tone="neutral">{t(`analytics.kind.${r.kind}`)}</Chip>
                    </TD>
                    <TD className="text-muted">
                      {t(`billing.method.${r.method}`, { defaultValue: r.method })}
                    </TD>
                    <TD numeric>
                      <Money minor={r.amount} />
                    </TD>
                    <TD>
                      <StatusPill
                        tone={statusTone(r.fiscal_status)}
                        label={t(`billing.fiscal.${r.fiscal_status}`, {
                          defaultValue: r.fiscal_status,
                        })}
                      />
                    </TD>
                  </TR>
                  {expandedId === r.payment_id ? <ExpandedRow row={r} colSpan={COLS} /> : null}
                </Fragment>
              ))}
            </tbody>
          )}
        </Table>
      </TableWrap>

      {/* Empty state */}
      {!revenue.isLoading && filtered.length === 0 ? (
        <div className="p-6">
          <EmptyState icon={DollarSign} title={t('analytics.incomeOverview.empty')} />
        </div>
      ) : null}
    </ExpandableCard>
  );
}
